// 공간별 공사 전/후 묶음 한 벌 — 초안 본문·편집 미리보기·공개 상세가 같은 규칙을 쓴다(전/후 태그 없는 장은 빠진다).

import type { PortfolioImageStage } from "./types";

export const OTHER_SPACE_LABEL = "기타";

// 목록 밖(설정에서 지운) 공간 코드 묶음의 공개 라벨 — 코드는 드러내지 않는다.
export const UNLISTED_SPACE_LABEL = "기타 공간";

// 편집 화면에서 지운 코드의 라벨 — 선택지(space-options)와 공간별 미리보기가 같이 쓴다.
export const DELETED_SPACE_LABEL = "삭제된 공간";

export const PAIR_STAGE_LABEL: Record<PortfolioImageStage, string> = { BEFORE: "공사 전", AFTER: "공사 후" };

export interface SpaceGroup<T> {
  // null 이면 공간이 없는 장을 모은 「기타」 · 목록 밖 코드는 코드마다 따로 묶인다.
  code: string | null;
  label: string;
  before: T[];
  after: T[];
}

type Tagged = { stage?: PortfolioImageStage | null; space?: string | null };

// 순서는 조직 목록(`spaces`) 순 → 목록 밖 코드(처음 나온 순) → 「기타」 · 공간 안 순서는 장 순서 그대로.
export const spaceGroupsOf = <T extends Tagged>(
  images: readonly T[],
  spaces: readonly { code: string; label: string }[],
  unlistedLabel: string = UNLISTED_SPACE_LABEL
): SpaceGroup<T>[] => {
  const order = new Map(spaces.map((s, i) => [s.code, i]));
  const unlisted = new Map<string, number>();
  const groups = new Map<string | null, SpaceGroup<T>>();
  for (const image of images) {
    if (image.stage !== "BEFORE" && image.stage !== "AFTER") continue;
    const code = image.space ? image.space : null;
    if (code !== null && !order.has(code) && !unlisted.has(code)) unlisted.set(code, spaces.length + unlisted.size);
    const group = groups.get(code) ?? {
      code,
      label: code === null ? OTHER_SPACE_LABEL : order.has(code) ? spaces[order.get(code) ?? 0].label : unlistedLabel,
      before: [],
      after: [],
    };
    (image.stage === "BEFORE" ? group.before : group.after).push(image);
    groups.set(code, group);
  }
  const rank = (g: SpaceGroup<T>): number =>
    g.code === null ? Number.MAX_SAFE_INTEGER : (order.get(g.code) ?? unlisted.get(g.code) ?? 0);
  return [...groups.values()].sort((a, b) => rank(a) - rank(b));
};

// 쌍 = 전·후가 둘 다 있는 공간 — 공개 상세와 초안 본문은 쌍만 싣는다.
export const isCompletePair = <T>(group: SpaceGroup<T>): boolean => group.before.length > 0 && group.after.length > 0;

export const spacePairsOf = <T extends Tagged>(
  images: readonly T[],
  spaces: readonly { code: string; label: string }[],
  unlistedLabel: string = UNLISTED_SPACE_LABEL
): SpaceGroup<T>[] => spaceGroupsOf(images, spaces, unlistedLabel).filter(isCompletePair);

// 전/후 태그가 없어 묶음에 못 들어간 장 수.
export const untaggedCountOf = (images: readonly Tagged[]): number =>
  images.filter((image) => image.stage !== "BEFORE" && image.stage !== "AFTER").length;

// 초안 본문 한 줄 — 「거실 — 공사 전 2장 / 공사 후 3장」.
export const pairLineOf = <T>(group: SpaceGroup<T>): string =>
  `${group.label} — ${PAIR_STAGE_LABEL.BEFORE} ${group.before.length}장 / ${PAIR_STAGE_LABEL.AFTER} ${group.after.length}장`;

// 대체텍스트 — 「거실 공사 전」, 같은 쪽에 여러 장이면 「거실 공사 전 2」.
export const pairAltOf = (label: string, stage: PortfolioImageStage, index: number, total: number): string =>
  total > 1 ? `${label} ${PAIR_STAGE_LABEL[stage]} ${index + 1}` : `${label} ${PAIR_STAGE_LABEL[stage]}`;
